import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import uuid from 'utils/uuid';
import { NProgress } from '@tanem/react-nprogress';

import { Modal, Toast, Button, CloseButton, Spinner } from '../base';
import ProgressBar from '../layout/progressBar';

const ModalContext = createContext();

export const ModalContextProvider = props => {
  const [modals, setModals] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [numberOfLoadings, setNumberOfLoadings] = useState(0);

  const spinnerRef = useRef();

  useEffect(() => {
    if (modals.length > 0) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
  }, [modals]);

  //
  // MODAL
  //
  const showModal = (children, params, key) => {
    setModals(value => [{ key, children, params: { closeable: true, ...params } }, ...value]);
    return key;
  };

  const handleOnShowModal = (children = null, params = {}, key = uuid()) => {
    return showModal(children, params, key);
  };

  const handleOnCloseModal = key => {
    setModals(value => value.filter(modal => modal.key !== key));
  };

  //
  // TOAST
  //
  const handleOnCloseToast = key => {
    setToasts(value => value.filter(toast => toast.key !== key));
  };

  const showToast = (children, params, key) => {
    setToasts(value => [{ key, children, params: { closeable: true, ...params } }, ...value]);

    if (params.timeout !== 0) {
      setTimeout(() => {
        handleOnCloseToast(key);
      }, params.timeout || 6000);
    }

    return key;
  };

  const handleOnShowToast = (children = null, params = {}, key = uuid()) => {
    return showToast(children, params, key);
  };

  //
  // CONFIRM
  //
  const handleOnShowConfirmModal = (children = null, params = {}, key = uuid()) => {
    const closeable = params.closeable ?? true;
    const confirmButtonType = params.type ?? 'primary';

    const handleOnCloseClick = () => {
      if (params.onCancel) {
        params.onCancel();
      }
    };

    const handleOnCancelClick = e => {
      e.preventDefault();

      if (params.onCancel) {
        params.onCancel();
      }

      handleOnCloseModal(key);
    };

    const handleOnConfirmClick = e => {
      e.preventDefault();

      if (params.onConfirm) {
        params.onConfirm();
      }

      handleOnCloseModal(key);
    };

    return showModal(
      <>{children}</>,
      {
        footer: () => (
          <>
            {closeable && (
              <Button type="outline" onClick={handleOnCancelClick}>
                {params.cancelLabel || 'Cancel'}
              </Button>
            )}
            <Button type={confirmButtonType} onClick={handleOnConfirmClick}>
              {params.confirmLabel || 'Confirm'}
            </Button>
          </>
        ),
        close: handleOnCloseClick,
        ...params,
      },
      key,
    );
  };

  //
  // SPINNER
  //
  const handleOnShowSpinner = (children = null, params = {}, key = uuid()) => {
    const closeable = params.closeable ?? false;

    handleOnCloseModal(spinnerRef.current);

    spinnerRef.current = showModal(
      <>
        <div className="text-center">
          <div style={{ height: 60 }}>
            <Spinner />
          </div>
          {children}
        </div>
      </>,
      {
        ...params,
        closeable,
      },
      key,
    );

    return spinnerRef.current;
  };

  // LOADING
  const handleOnLoadingChange = isLoading => {
    const diff = isLoading ? 1 : -1;

    setNumberOfLoadings(value => Math.max(value + diff, 0));
  };

  const wrappedModals = modals.map(modal => ({
    ...modal,
    close: () => {
      if (modal.params?.close) {
        modal.params.close();
      }
      handleOnCloseModal(modal.key);
    },
  }));

  const wrappedToasts = toasts.map(toast => ({
    ...toast,
    close: () => {
      if (toast.params?.close) {
        toast.params.close();
      }
      handleOnCloseToast(toast.key);
    },
  }));

  return (
    <ModalContext.Provider
      value={{
        show: handleOnShowModal,
        close: handleOnCloseModal,
        toast: handleOnShowToast,
        spinner: handleOnShowSpinner,
        hideSpinner: () => handleOnCloseModal(spinnerRef.current),
        confirm: handleOnShowConfirmModal,
        setLoading: handleOnLoadingChange,
      }}
    >
      <NProgress
        isAnimating={props.isRouteChanging || numberOfLoadings > 0}
        animationDuration={100}
        incrementDuration={100}
        minimum={0}
      >
        {({ animationDuration, isFinished, progress }) => (
          <ProgressBar
            color={props.progressBarColor}
            animationDuration={animationDuration}
            progress={progress}
            isFinished={isFinished}
          />
        )}
      </NProgress>

      {wrappedModals.length > 0 && (
        <>
          {wrappedModals.map(modal => (
            <Modal.Wrapper key={modal.key}>
              <Modal.Dialog className={modal.params.className}>
                <Modal.Content>
                  <Modal.Header>
                    {modal.params.title && <Modal.Title>{modal.params.title}</Modal.Title>}
                    {modal.params.closeable && (
                      <CloseButton onClick={modal.close}>
                        <span aria-hidden="true">&times;</span>
                      </CloseButton>
                    )}
                  </Modal.Header>
                  <Modal.Body>
                    {React.Children.map(modal.children, item => ({
                      ...item,
                      props: { ...item.props, close: modal.close, key: modal.key },
                    }))}
                  </Modal.Body>
                  {modal.params.footer && (
                    <Modal.Footer className={modal.params.footerClassName}>
                      {modal.params.footer({ close: modal.close })}
                    </Modal.Footer>
                  )}
                </Modal.Content>
              </Modal.Dialog>
            </Modal.Wrapper>
          ))}
        </>
      )}

      {wrappedToasts.length > 0 && (
        <>
          {wrappedToasts.map(toast => (
            <Toast.Wrapper key={toast.key} type={toast.params.type} visible>
              <Toast.Body>{toast.children}</Toast.Body>
              {toast.params.closeable && (
                <CloseButton onClick={toast.close}>
                  <span aria-hidden="true">&times;</span>
                </CloseButton>
              )}
            </Toast.Wrapper>
          ))}
        </>
      )}
      {props.children}
      <Modal.Backdrop visible={wrappedModals.length > 0} />
    </ModalContext.Provider>
  );
};

ModalContextProvider.propTypes = {
  isRouteChanging: PropTypes.bool,
  progressBarColor: PropTypes.string,
  children: PropTypes.node,
};

const useModalContext = () => {
  return useContext(ModalContext);
};

export default useModalContext;
